import { useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { UI, DISPLAY } from '../styles/tl5';
import {
  useListCareerOpeningsQuery,
  useSubmitCareerApplicationMutation,
} from '../store/siteContentApi';
// The reference's Careers hero is byte-identical to the About hero
// (same md5), so it reuses that asset rather than shipping a copy.
import heroImg from '../assets/img/v5/about-hero.jpg';

// Careers — built to the v5 reference, and only what the reference has:
//   1. hero
//   2. three "why work here" cards
//   3. open roles — department chips + a list
//
// Two deliberate departures, both because the reference is a prototype and
// we have the real thing already working:
//   · its seven roles are hardcoded sample copy; ours come from
//     /careers/openings, which the admin curates. The chips are derived
//     from whatever departments actually have a posting.
//   · its Apply is a mailto. Ours selects the role and drops you into the
//     application form below, which POSTs to /careers/applications with
//     the CV attached. That form has no counterpart in the reference, so
//     it keeps the reference's own card idiom.
//
// Dropped with the old page, since the reference has none of them: the
// stats bar, the six-pillar "Why TrustLab" grid, and the hero's floating
// role cards.

const WHY = [
  {
    t: 'Work that matters',
    d: 'Every result you touch helps a doctor make a decision for a real patient.',
    bg: '#FFFFFF',
    fg: 'var(--tl-g9)',
    body: 'var(--tl-muted)',
  },
  {
    t: 'Learn from specialists',
    d: 'Genomics, cytogenetics and histopathology under one roof in Hyderabad.',
    bg: 'var(--tl-teal)',
    fg: '#FFFFFF',
    body: '#E6FAF5',
  },
  {
    t: 'Grow with us',
    d: '14 branches and growing. New centres mean new leadership roles.',
    bg: 'var(--tl-yellow)',
    fg: 'var(--tl-g9)',
    body: '#3A4020',
  },
];

const HR_EMAIL = 'hr@mytrustlab.com';
const MAX_CV_BYTES = 5 * 1024 * 1024;

const ALL = 'All';

// A 40 KB CV reads as "0.0 MB" if everything is forced into megabytes.
const fileSize = (bytes) =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

// ── shared fragments ──────────────────────────────────────────────────
const card = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  p: 'clamp(20px, 3.4vw, 32px)',
  borderRadius: '28px',
  bgcolor: '#FFFFFF',
};

const cardTitle = {
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 24,
  lineHeight: 1.15,
  letterSpacing: '-.01em',
};

const h2 = {
  m: 0,
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 'clamp(32px, 4vw, 48px)',
  lineHeight: 1.05,
  letterSpacing: '-.02em',
  color: 'var(--tl-g9)',
};

const field = {
  border: '1.5px solid var(--tl-line)',
  borderRadius: '14px',
  outline: 0,
  font: 'inherit',
  fontFamily: UI,
  fontSize: 16,
  p: '13px 14px',
  color: 'var(--tl-g9)',
  bgcolor: '#FFFFFF',
  width: '100%',
  minWidth: 0,
  '&:focus': { borderColor: 'var(--tl-teal)' },
};

const fieldLabel = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  minWidth: 0,
  fontFamily: UI,
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--tl-muted-2)',
};

const EMPTY_FORM = {
  full_name: '',
  email: '',
  phone: '',
  position: '',
  experience: '',
  current_location: '',
  qualification: '',
  notice_period: '',
  message: '',
  source: '',
  consent: false,
};

const Careers = () => {
  const { data: openings = [], isLoading } = useListCareerOpeningsQuery();
  const [submit, { isLoading: sending }] = useSubmitCareerApplicationMutation();

  const [dept, setDept] = useState(ALL);
  const [form, setForm] = useState(EMPTY_FORM);
  const [cv, setCv] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [status, setStatus] = useState({ kind: '', message: '', mailto: '' });
  const cvInput = useRef(null);

  // Chips only list departments that actually have a posting, so they
  // can never show an empty filter.
  const depts = useMemo(() => {
    const set = new Set(openings.map((o) => o.department).filter(Boolean));
    return [ALL, ...Array.from(set).sort()];
  }, [openings]);

  const roles = useMemo(() => {
    const picked = dept === ALL ? openings : openings.filter((o) => o.department === dept);
    return [...picked].sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
  }, [openings, dept]);

  const selected = openings.find((o) => o.job_id === form.position) || null;

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const pickCv = (file) => {
    if (!file) return;
    if (file.size > MAX_CV_BYTES) {
      setStatus({ kind: 'error', message: 'That file is over 5 MB. Please attach a smaller one.', mailto: '' });
      setCv(null);
      if (cvInput.current) cvInput.current.value = '';
      return;
    }
    setStatus({ kind: '', message: '', mailto: '' });
    setCv(file);
  };

  // "Apply" on a role row: remember which one, then drop the applicant
  // into the form with the role already chosen.
  const applyTo = (role) => {
    setForm((f) => ({ ...f, position: role.job_id }));
    setStatus({ kind: '', message: '', mailto: '' });
    document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => document.getElementById('careers-name')?.focus(), 500);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return setStatus({ kind: 'error', message: 'Please enter your name', mailto: '' });
    if (form.phone.replace(/\D/g, '').length < 10) {
      return setStatus({ kind: 'error', message: 'Please enter a 10-digit mobile number', mailto: '' });
    }
    if (!form.position) return setStatus({ kind: 'error', message: 'Please choose the role you are applying for', mailto: '' });
    if (!cv) return setStatus({ kind: 'error', message: 'Please attach your CV (PDF, DOC or DOCX)', mailto: '' });
    if (!form.consent) {
      return setStatus({ kind: 'error', message: 'Please tick the consent box so we can process your application', mailto: '' });
    }

    // Everything validated — clear the last error so it isn't still on
    // screen behind "Sending…".
    setStatus({ kind: '', message: '', mailto: '' });

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v == null ? '' : String(v)));
    fd.append('cv', cv);
    fd.append('source_page', window.location.href);

    try {
      const resp = await submit(fd).unwrap();
      setStatus({
        kind: 'success',
        message: resp?.message || 'Application received. We acknowledge every application within three working days.',
        mailto: '',
      });
      setForm(EMPTY_FORM);
      setCv(null);
      if (cvInput.current) cvInput.current.value = '';
    } catch (err) {
      const subject = selected?.title || 'Open application';
      setStatus({
        kind: 'error',
        message: `${err?.data?.message || 'We could not submit the form just now.'} You can email your CV to ${HR_EMAIL} instead.`,
        mailto: `mailto:${HR_EMAIL}?subject=${encodeURIComponent(`Application — ${subject}`)}`,
      });
    }
  };

  return (
    <Box>
      {/* ── 1. hero ──────────────────────────────────────────────────── */}
      <Box component="section" sx={{ pt: 2, px: 2 }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: 1320,
            mx: 'auto',
            minHeight: { xs: 380, md: 460 },
            display: 'flex',
            alignItems: 'end',
            borderRadius: '36px',
            overflow: 'hidden',
            bgcolor: 'var(--tl-g9)',
          }}
        >
          <Box
            component="img"
            src={heroImg}
            alt=""
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(90deg, rgba(7,33,29,0.92) 0%, rgba(7,33,29,0.72) 50%, rgba(7,33,29,0.2) 100%)',
            }}
          />
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              gap: 2.25,
              maxWidth: 880,
              width: '100%',
              p: 'clamp(28px, 5vw, 56px)',
              color: '#FFFFFF',
            }}
          >
            <Box
              component="span"
              sx={{
                alignSelf: 'start',
                px: '10px',
                py: '6px',
                borderRadius: '999px',
                bgcolor: 'var(--tl-yellow)',
                color: 'var(--tl-g9)',
                fontFamily: UI,
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              Careers at TrustLab
            </Box>
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: 'clamp(34px, 5.4vw, 72px)',
                lineHeight: 0.95,
                letterSpacing: '-.035em',
                textWrap: 'balance',
                color: '#FFFFFF',
              }}
            >
              Build trust, one sample at a time.
            </Typography>
            <Typography
              sx={{ m: 0, fontFamily: UI, fontSize: 18, lineHeight: 1.55, color: '#D5E7E2', maxWidth: 600 }}
            >
              Join the people who collect, test and verify results for families across India.
            </Typography>
            {/* An in-page anchor, so it needs a plain <a>, not a Link. */}
            <Box
              component="a"
              href="#roles"
              sx={{
                alignSelf: 'start',
                borderRadius: '16px',
                p: '15px 22px',
                bgcolor: 'var(--tl-yellow)',
                color: 'var(--tl-g9)',
                fontFamily: UI,
                fontSize: 16,
                fontWeight: 800,
                textDecoration: 'none',
                '&:hover': { bgcolor: 'var(--tl-yellow-d)' },
              }}
            >
              See open roles
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        component="main"
        sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 1.5, pb: 9, display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        {/* ── 2. why work here ───────────────────────────────────────── */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
            gap: 1.5,
          }}
        >
          {WHY.map((w) => (
            <Box key={w.t} component="section" sx={{ ...card, bgcolor: w.bg, minHeight: 200 }}>
              <Box component="span" sx={{ ...cardTitle, color: w.fg }}>{w.t}</Box>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 16, lineHeight: 1.6, color: w.body }}>
                {w.d}
              </Box>
            </Box>
          ))}
        </Box>

        {/* ── 3. open roles ──────────────────────────────────────────── */}
        <Box
          component="section"
          id="roles"
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 5, scrollMarginTop: '100px' }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'end',
              flexWrap: 'wrap',
              gap: 1.5,
              px: 1,
            }}
          >
            <Typography component="h2" sx={h2}>Open roles</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {depts.map((d) => {
                const on = d === dept;
                return (
                  <Box
                    key={d}
                    component="button"
                    type="button"
                    onClick={() => setDept(d)}
                    aria-pressed={on}
                    sx={{
                      border: 0,
                      cursor: 'pointer',
                      borderRadius: '999px',
                      p: '10px 16px',
                      whiteSpace: 'nowrap',
                      fontFamily: UI,
                      fontSize: 14,
                      fontWeight: 700,
                      bgcolor: on ? 'var(--tl-g9)' : '#FFFFFF',
                      color: on ? '#FFFFFF' : 'var(--tl-g9)',
                    }}
                  >
                    {d}
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box component="section" sx={{ ...card, gap: 0 }}>
            {isLoading && [0, 1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  py: 2.25,
                  borderBottom: '1px solid #EEF3F1',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
                  <Box sx={{ height: 18, width: '48%', borderRadius: '6px', bgcolor: 'var(--tl-mint-2)' }} />
                  <Box sx={{ height: 13, width: '30%', borderRadius: '6px', bgcolor: 'var(--tl-mint)' }} />
                </Box>
                <Box sx={{ height: 40, width: 86, borderRadius: '16px', bgcolor: 'var(--tl-mint)' }} />
              </Box>
            ))}

            {!isLoading && roles.length === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 2.25 }}>
                <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, fontSize: 18, color: 'var(--tl-g9)' }}>
                  {openings.length ? `Nothing open in ${dept} right now` : 'No roles are open right now'}
                </Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-muted-2)' }}>
                  New postings go up as branches open. Email your CV and we will keep it on file.
                </Box>
              </Box>
            )}

            {!isLoading && roles.map((r) => (
              <Box
                key={r.job_id || r._id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                  py: 2.25,
                  borderBottom: '1px solid #EEF3F1',
                }}
              >
                <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                  <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, fontSize: 18, color: 'var(--tl-g9)' }}>
                    {r.title}
                  </Box>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-muted-2)' }}>
                    {[r.department, r.location, r.type, r.experience].filter(Boolean).join(' · ')}
                  </Box>
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={() => applyTo(r)}
                  sx={{
                    border: 0,
                    cursor: 'pointer',
                    borderRadius: '16px',
                    p: '11px 18px',
                    bgcolor: 'var(--tl-mint)',
                    color: 'var(--tl-g9)',
                    fontFamily: UI,
                    fontSize: 14,
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: 'var(--tl-mint-2)' },
                  }}
                >
                  Apply
                </Box>
              </Box>
            ))}

            <Box component="span" sx={{ fontFamily: UI, fontSize: 12, color: '#7D918C', pt: 1.75 }}>
              Don’t see your role? Send your CV through the form below and pick the closest
              department — we keep applications on file for six months.
            </Box>
          </Box>
        </Box>

        {/* ── 4. apply ───────────────────────────────────────────────────
            Not in the reference, which applies by mailto. /careers/
            applications already takes the CV and the full detail that HR
            screens on, so the form stays and borrows the card idiom. */}
        <Box
          component="section"
          id="apply"
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 5, scrollMarginTop: '100px' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 1 }}>
            <Typography component="h2" sx={h2}>Apply</Typography>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 16, lineHeight: 1.6, color: 'var(--tl-muted)' }}>
              One form, one CV. We read every application and reply within three working days.
            </Box>
          </Box>

          <Box component="section" sx={card}>
            {status.kind === 'success' ? (
              <>
                <Box component="span" sx={{ ...cardTitle, color: 'var(--tl-g9)' }}>Application received</Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 16, lineHeight: 1.65, color: 'var(--tl-muted)' }}>
                  {status.message}
                </Box>
              </>
            ) : (
              <Box component="form" onSubmit={onSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
                {selected && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      p: 2,
                      borderRadius: '18px',
                      bgcolor: 'var(--tl-mint)',
                    }}
                  >
                    <Box component="span" sx={{ fontFamily: UI, fontSize: 12, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--tl-teal-d)' }}>
                      Applying for
                    </Box>
                    <Box component="span" sx={{ fontFamily: UI, fontSize: 17, fontWeight: 800, color: 'var(--tl-g9)' }}>
                      {selected.title}
                    </Box>
                    {selected.description && (
                      <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.6, color: 'var(--tl-muted)' }}>
                        {selected.description}
                      </Box>
                    )}
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
                    gap: 1.25,
                  }}
                >
                  <Box component="label" htmlFor="careers-name" sx={fieldLabel}>
                    Your name
                    <Box component="input" id="careers-name" value={form.full_name} onChange={set('full_name')} placeholder="Full name" sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Mobile
                    <Box component="input" value={form.phone} onChange={set('phone')} placeholder="+91" inputMode="tel" sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Email
                    <Box component="input" value={form.email} onChange={set('email')} placeholder="name@email.com" type="email" sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Role
                    <Box component="select" value={form.position} onChange={set('position')} sx={{ ...field, cursor: 'pointer' }}>
                      <option value="">Choose a role</option>
                      {openings.map((o) => (
                        <option key={o.job_id || o._id} value={o.job_id}>
                          {o.title} — {o.location}
                        </option>
                      ))}
                    </Box>
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Years of experience
                    <Box component="input" value={form.experience} onChange={set('experience')} placeholder="e.g. 3" inputMode="numeric" sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Where you live now
                    <Box component="input" value={form.current_location} onChange={set('current_location')} placeholder="City" sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Highest qualification
                    <Box component="input" value={form.qualification} onChange={set('qualification')} placeholder="e.g. B.Sc MLT" sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Notice period
                    <Box component="input" value={form.notice_period} onChange={set('notice_period')} placeholder="e.g. 30 days" sx={field} />
                  </Box>
                </Box>

                <Box component="label" sx={fieldLabel}>
                  Anything you want us to know (optional)
                  <Box
                    component="textarea"
                    value={form.message}
                    onChange={set('message')}
                    rows={3}
                    placeholder="A line about the work you have done"
                    sx={{ ...field, resize: 'vertical', lineHeight: 1.6 }}
                  />
                </Box>

                {/* CV uploader — PDF/DOC/DOCX, 5 MB ceiling, matching multer's. */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 13, fontWeight: 700, color: 'var(--tl-muted-2)' }}>
                    Your CV
                  </Box>
                  <Box
                    component="label"
                    htmlFor="careers-cv"
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(e) => { e.preventDefault(); setDragging(false); pickCv(e.dataTransfer?.files?.[0]); }}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      cursor: 'pointer',
                      p: 3,
                      textAlign: 'center',
                      borderRadius: '18px',
                      border: `2px dashed ${dragging ? 'var(--tl-teal)' : 'var(--tl-line-2)'}`,
                      bgcolor: dragging ? 'var(--tl-teal-50)' : 'var(--tl-mint)',
                    }}
                  >
                    <Box component="span" sx={{ fontFamily: UI, fontSize: 15, fontWeight: 800, color: 'var(--tl-g9)' }}>
                      {cv ? cv.name : 'Drop your CV here, or tap to choose'}
                    </Box>
                    <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>
                      {cv
                        ? `${fileSize(cv.size)} · tap to replace`
                        : 'PDF, DOC or DOCX, up to 5 MB'}
                    </Box>
                    <Box
                      component="input"
                      id="careers-cv"
                      ref={cvInput}
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={(e) => pickCv(e.target.files?.[0])}
                      sx={{ position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px', overflow: 'hidden', clipPath: 'inset(50%)', border: 0 }}
                    />
                  </Box>
                </Box>

                <Box component="label" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, cursor: 'pointer' }}>
                  <Box
                    component="input"
                    type="checkbox"
                    checked={form.consent}
                    onChange={set('consent')}
                    sx={{ width: 20, height: 20, mt: '2px', flexShrink: 0, accentColor: 'var(--tl-teal-d)', cursor: 'pointer' }}
                  />
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.6, color: 'var(--tl-muted)' }}>
                    I agree that TrustLab may store these details and my CV to consider me for
                    this and other roles for up to six months.
                  </Box>
                </Box>

                {status.kind === 'error' && (
                  <Box component="span" role="alert" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, color: '#B23B37' }}>
                    {status.message}{' '}
                    {status.mailto && (
                      <Box component="a" href={status.mailto} sx={{ color: 'var(--tl-teal-d)' }}>
                        Open an email
                      </Box>
                    )}
                  </Box>
                )}

                <Box
                  component="button"
                  type="submit"
                  disabled={sending}
                  sx={{
                    alignSelf: 'start',
                    border: 0,
                    cursor: sending ? 'default' : 'pointer',
                    borderRadius: '16px',
                    p: '15px 22px',
                    bgcolor: sending ? '#B9C9C4' : 'var(--tl-g9)',
                    color: '#FFFFFF',
                    fontFamily: UI,
                    fontSize: 16,
                    fontWeight: 800,
                  }}
                >
                  {sending ? 'Sending…' : 'Send application'}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Careers;
